import React, { useEffect, useState } from "react";
import { FloppyDisk, X } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function AdminSettings() {
  const [form, setForm] = useState(null);

  useEffect(() => { api.get("/site").then((r) => setForm(r.data)); }, []);

  if (!form) return <div className="p-10 text-white/40">Loading…</div>;

  const save = async () => {
    try {
      await api.put("/site", form);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addArea = () => setForm({ ...form, service_areas: [...(form.service_areas || []), ""] });
  const updateArea = (i, v) => {
    const arr = [...form.service_areas];
    arr[i] = v;
    setForm({ ...form, service_areas: arr });
  };
  const removeArea = (i) => setForm({ ...form, service_areas: form.service_areas.filter((_, idx) => idx !== i) });

  const addSpecialty = () => setForm({ ...form, specialties: [...(form.specialties || []), ""] });
  const updateSpecialty = (i, v) => {
    const arr = [...(form.specialties || [])];
    arr[i] = v;
    setForm({ ...form, specialties: arr });
  };
  const removeSpecialty = (i) => setForm({ ...form, specialties: (form.specialties || []).filter((_, idx) => idx !== i) });

  return (
    <div className="p-8 md:p-10 max-w-4xl" data-testid="admin-settings">
      <div className="mb-8">
        <div className="overline mb-2">Configuration</div>
        <h1 className="font-display text-5xl uppercase text-white leading-none">Site settings</h1>
        <p className="text-white/60 mt-2 text-sm">Contact details, hours, and service area shown on the public site.</p>
      </div>

      <div className="admin-card space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><div className="admin-label">Phone</div><input className="admin-input" value={form.phone || ""} onChange={update("phone")} data-testid="s-phone" /></div>
          <div><div className="admin-label">Email</div><input className="admin-input" value={form.email || ""} onChange={update("email")} data-testid="s-email" /></div>
          <div className="md:col-span-2"><div className="admin-label">Address / Business Location</div><input className="admin-input" value={form.address || ""} onChange={update("address")} data-testid="s-address" /></div>
          <div><div className="admin-label">Showroom location</div><input className="admin-input" value={form.showroom_address || ""} onChange={update("showroom_address")} placeholder="e.g., Loganville, PA" data-testid="s-showroom" /></div>
          <div><div className="admin-label">Tagline</div><input className="admin-input" value={form.tagline || ""} onChange={update("tagline")} data-testid="s-tagline" /></div>
          <div><div className="admin-label">Service radius (miles)</div><input type="number" className="admin-input" value={form.service_radius_miles || 0} onChange={(e) => setForm({ ...form, service_radius_miles: parseInt(e.target.value) || 0 })} data-testid="s-radius" /></div>
        </div>
        <div className="pt-5 border-t border-[#2a2a2a]">
          <div className="admin-label">Hours</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input className="admin-input" placeholder="Mon–Fri" value={form.hours_mon_fri || ""} onChange={update("hours_mon_fri")} data-testid="s-hours-mf" />
            <input className="admin-input" placeholder="Sat" value={form.hours_sat || ""} onChange={update("hours_sat")} data-testid="s-hours-sat" />
            <input className="admin-input" placeholder="Sun" value={form.hours_sun || ""} onChange={update("hours_sun")} data-testid="s-hours-sun" />
          </div>
        </div>
        <div className="pt-5 border-t border-[#2a2a2a] grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><div className="admin-label">Facebook URL</div><input className="admin-input" value={form.facebook_url || ""} onChange={update("facebook_url")} data-testid="s-fb" /></div>
          <div><div className="admin-label">Instagram URL</div><input className="admin-input" value={form.instagram_url || ""} onChange={update("instagram_url")} data-testid="s-ig" /></div>
        </div>

        <div className="pt-5 border-t border-[#2a2a2a]">
          <h3 className="font-display text-2xl uppercase text-white mb-1">Lead notifications</h3>
          <p className="text-white/40 text-xs mb-5">Automated emails sent when someone submits your contact form. Delivered from your Gmail via app-password (already configured).</p>
          <div>
            <div className="admin-label">Notify these email addresses (comma-separated)</div>
            <textarea
              rows={2}
              className="admin-input"
              value={(form.notification_recipients || []).join(", ")}
              onChange={(e) => setForm({ ...form, notification_recipients: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
              placeholder="you@example.com, bryan@example.com"
              data-testid="s-notify-recipients"
            />
            <div className="text-white/40 text-[10px] font-mono mt-1">{(form.notification_recipients || []).length} recipient(s)</div>
          </div>
          <label className="flex items-center gap-2 mt-4 cursor-pointer" data-testid="s-autoreply-toggle">
            <input
              type="checkbox"
              className="w-4 h-4 accent-atc-red"
              checked={!!form.auto_reply_enabled}
              onChange={(e) => setForm({ ...form, auto_reply_enabled: e.target.checked })}
            />
            <span className="text-white/80 text-sm">Send an auto-reply to the lead confirming we received their message</span>
          </label>
        </div>
        <div className="pt-5 border-t border-[#2a2a2a]">
          <h3 className="font-display text-2xl uppercase text-white mb-1">SEO & Branding</h3>
          <p className="text-white/40 text-xs mb-5">Controls how Google and social media see your site.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div><div className="admin-label">Business name (for Google)</div><input className="admin-input" value={form.business_name || ""} onChange={update("business_name")} data-testid="s-bizname" /></div>
            <div><div className="admin-label">Legal name</div><input className="admin-input" value={form.legal_name || ""} onChange={update("legal_name")} data-testid="s-legalname" /></div>
            <div className="md:col-span-2"><div className="admin-label">Site URL (production)</div><input className="admin-input" value={form.site_url || ""} onChange={update("site_url")} placeholder="https://yourdomain.com" data-testid="s-siteurl" /></div>
            <div className="md:col-span-2">
              <div className="admin-label">Default meta description (shown by Google in search results, 150–160 chars)</div>
              <textarea rows={3} className="admin-input" value={form.meta_description || ""} onChange={update("meta_description")} data-testid="s-metadesc" />
              <div className="text-white/40 text-[10px] font-mono mt-1">{(form.meta_description || "").length} chars</div>
            </div>
            <div className="md:col-span-2"><div className="admin-label">Open Graph image (shown when site is shared on Facebook/Twitter) — paste full URL or storage path</div><input className="admin-input" value={form.og_image || ""} onChange={update("og_image")} placeholder="https://… or atc-home-pros/…" data-testid="s-ogimage" /></div>
            <div><div className="admin-label">Founded year</div><input type="number" className="admin-input" value={form.founding_year || ""} onChange={(e) => setForm({ ...form, founding_year: parseInt(e.target.value) || 0 })} data-testid="s-founded" /></div>
            <div><div className="admin-label">Google Search Console verification (optional)</div><input className="admin-input" value={form.google_site_verification || ""} onChange={update("google_site_verification")} placeholder="abc123…" data-testid="s-gsc" /></div>
          </div>
          <div className="mt-5">
            <div className="admin-label">Full address (used in LocalBusiness schema for Google Maps)</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input className="admin-input md:col-span-2" placeholder="Street address" value={form.street_address || ""} onChange={update("street_address")} data-testid="s-street" />
              <input className="admin-input" placeholder="City" value={form.city || ""} onChange={update("city")} data-testid="s-city" />
              <div className="grid grid-cols-2 gap-2">
                <input className="admin-input" placeholder="State" value={form.region || ""} onChange={update("region")} data-testid="s-region" />
                <input className="admin-input" placeholder="ZIP" value={form.postal_code || ""} onChange={update("postal_code")} data-testid="s-zip" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <input className="admin-input" type="number" step="any" placeholder="Latitude (optional)" value={form.latitude ?? ""} onChange={(e) => setForm({ ...form, latitude: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="s-lat" />
              <input className="admin-input" type="number" step="any" placeholder="Longitude (optional)" value={form.longitude ?? ""} onChange={(e) => setForm({ ...form, longitude: e.target.value === "" ? null : parseFloat(e.target.value) })} data-testid="s-lng" />
            </div>
          </div>
        </div>
        <div className="pt-5 border-t border-[#2a2a2a]">
          <div className="flex items-center justify-between mb-3">
            <div className="admin-label !mb-0">Service areas</div>
            <button onClick={addArea} className="font-heading uppercase tracking-widest text-xs text-atc-red hover:text-white" data-testid="add-area">+ Add</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(form.service_areas || []).map((a, i) => (
              <div key={i} className="flex gap-2">
                <input className="admin-input flex-1" value={a} onChange={(e) => updateArea(i, e.target.value)} data-testid={`s-area-${i}`} />
                <button onClick={() => removeArea(i)} className="border border-[#333] hover:border-atc-red p-2"><X size={14} weight="bold" /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="pt-5 border-t border-[#2a2a2a]">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="admin-label !mb-0">Specialties</div>
              <div className="text-white/40 text-xs mt-1">Shown as the "Everything under one roof" list on the homepage.</div>
            </div>
            <button onClick={addSpecialty} className="font-heading uppercase tracking-widest text-xs text-atc-red hover:text-white" data-testid="add-specialty">+ Add</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {(form.specialties || []).map((s, i) => (
              <div key={i} className="flex gap-2">
                <input className="admin-input flex-1" value={s} onChange={(e) => updateSpecialty(i, e.target.value)} data-testid={`s-specialty-${i}`} />
                <button onClick={() => removeSpecialty(i)} className="border border-[#333] hover:border-atc-red p-2"><X size={14} weight="bold" /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="pt-5 border-t border-[#2a2a2a]">
          <button onClick={save} className="btn-primary" data-testid="save-settings-btn"><FloppyDisk size={18} weight="bold" /> Save settings</button>
        </div>
      </div>
    </div>
  );
}
